/**
 * Tasks Hook
 *
 * Fetches tasks for the current view via TanStack Query.
 * Special slug 'snoozed' fetches tasks via the snoozedOnly param directly.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { Task, View } from '../types';
import { getViewTasks } from '../api/client';
import { getSnoozedTasks, getCompletedTasks } from '../api/tasks';

interface TasksQueryResult {
  tasks: Task[];
  view: View | null;
}

export function useTasks(viewSlug: string, showCompleted = false) {
  const isSnoozedView = viewSlug === 'snoozed';
  const isCompletedView = viewSlug === 'completed';

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<TasksQueryResult>({
    queryKey: isCompletedView ? ['tasks', 'completed'] : isSnoozedView ? ['tasks', 'snoozed'] : ['tasks', viewSlug, showCompleted],
    queryFn: async (): Promise<TasksQueryResult> => {
      if (isCompletedView) {
        const result = await getCompletedTasks();
        return { tasks: result.data, view: null };
      }
      if (isSnoozedView) {
        const result = await getSnoozedTasks();
        return { tasks: result.data, view: null };
      }
      const result = await getViewTasks(viewSlug, { includeCompleted: showCompleted });
      return { tasks: result.tasks, view: result.view };
    },
    enabled: !!viewSlug,
    placeholderData: keepPreviousData,
  });

  const tasks: Task[] = data?.tasks ?? [];
  const view: View | null = data?.view ?? null;

  return {
    tasks,
    view,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load tasks') : null,
    refetch,
  };
}
